import { SearchFiltersQueryParams } from 'components/FilterMenu/SearchFilter/SearchFilter';
import { WebRoute } from 'config/fe-routes';
import Router from 'next/router';
import querystring, { ParsedUrlQuery, ParsedUrlQueryInput } from 'querystring';

export type RedirectParams = {
  href: string;
  as?: string;
  query?: string | ParsedUrlQueryInput | null | undefined;
  hideQueryKeys?: string[];
  opts?: {
    shallow?: boolean;
    scroll?: boolean;
  };
};

/**
 * Next.js redirect without reloading.
 * @param href - the path inside pages directory.
 * @param as - the path that will be rendered in the browser URL bar
 * @param query - query params
 * @param opts - additional options for Next.js router
 * @param replace
 */
export function redirectTo({ href, as, query, hideQueryKeys, opts }: RedirectParams, replace?: boolean): Promise<boolean> {
  const redirect = !replace ? Router.push : Router.replace;
  let hrefQuery = query;

  // Add queryparams to href stating that replace happened (we need to know that in route-history-tracker)
  // It is not visible in browser url (because "as" url does not have "?replace=true" query param)
  if (replace) {
    if (typeof query === 'string') {
      hrefQuery = querystring.parse(query);
    }

    hrefQuery = {
      ...(hrefQuery as ParsedUrlQuery),
      replace: true,
    };
  }

  if (!query && !replace) {
    return redirect(href, as, opts || {});
  }

  // Hide query keys that we don't want to show in browser url.
  const maskedQuery = typeof query === 'string' ? { ...querystring.parse(query) } : { ...query };

  hideQueryKeys?.forEach((key) => delete maskedQuery?.[key]);

  return redirect({ pathname: href, query: hrefQuery }, { pathname: as, query: maskedQuery }, opts || {});
}

export function silentUrlReplace({ href, as }: WebRoute, title: string, type?: string) {
  try {
    window.history.replaceState({ url: href, as, type: type || title }, title, as);
  } catch (e) {
    console.error(e);
  }
}

/**
 * Gets url query params. If url missing, gets current url query params.
 * @param url
 */
export function getQueryParams(url?: string) {
  if (!url && typeof window !== 'undefined') {
    url = window.location.search;
  } else if (typeof window === 'undefined') {
    return {};
  }

  // Get querystring
  const [, queryString] = url.split('?');

  return querystring.parse(queryString);
}

/**
 * Get specific query param
 * @param param
 * @param url
 */
export function getQueryParam(param: string, url?: string) {
  const queryParams = getQueryParams(url);

  return queryParams && queryParams[param];
}

/**
 * Stringifies query params like usual querystring library but with the exception of deleting keys that have values of null
 * @param queryParams
 */
export function stringifyQueryParams(queryParams) {
  Object.keys(queryParams).forEach((queryParamKey) => {
    if (queryParams[queryParamKey] === null) {
      delete queryParams[queryParamKey];
    }
  });
  const queryString = querystring.stringify(queryParams);

  return queryString ? '?' + queryString : queryString;
}

/**
 * Removes undefined queryparams
 * @param queryParams
 */
export function cleanQueryParams(queryParams, excludedParams?) {
  const cleanedQueryParams = { ...queryParams };

  Object.keys(cleanedQueryParams).forEach((queryParamKey) => {
    if (excludedParams && excludedParams.includes(queryParamKey)) {
      return;
    }
    if (!cleanedQueryParams[queryParamKey] && cleanedQueryParams[queryParamKey] !== 0) {
      delete cleanedQueryParams[queryParamKey];
    }
  });

  return cleanedQueryParams;
}

/**
 * Removes all query params that are not allowed
 * @param queryParams
 * @param allowedQueryParamKeys
 */
export function extractQueryParams(queryParams, allowedQueryParamKeys: Array<string>): SearchFiltersQueryParams {
  const queryParamsClone = { ...(queryParams || {}) };

  Object.keys(queryParamsClone || {}).forEach((queryParamKey) => {
    if (!allowedQueryParamKeys.includes(queryParamKey)) {
      delete queryParamsClone[queryParamKey];
    }
  });

  return cleanQueryParams(queryParamsClone);
}

/**
 * Checks if query params changed
 * @param queryParams
 * @param newQueryParams
 */
export function queryParamsChanged(queryParams, newQueryParams) {
  return (
    Object.keys(newQueryParams || {}).some((queryParamKey) => newQueryParams?.[queryParamKey] !== queryParams?.[queryParamKey]) ||
    Object.keys(queryParams || {}).some((queryParamKey) => queryParams?.[queryParamKey] !== newQueryParams?.[queryParamKey])
  );
}
